"use client";

import { createContext, useContext, useState, ReactNode } from "react";
import type {
  CatalogItem,
  SelectedFiltersType,
} from "@/lib/services/catalog/catalog-types";

// Katalog için gerekli tüm state'leri ve fonksiyonları içeren context
interface CatalogContextType {
  // State değerleri
  searchValue: string;
  selectedTags: string[];
  isFilterSectionVisible: boolean;
  isSortOpen: boolean;
  isGridOpen: boolean;
  sortOption: string;
  viewMode: "grid4" | "grid6" | "grid8" | "table" | "gallery";
  selectedFilters: SelectedFiltersType;
  filteredItems: CatalogItem[];

  // Fonksiyonlar
  setSearchValue: (value: string) => void;
  toggleTag: (tag: string) => void;
  clearFilters: () => void;
  handleFilterChange: (category: string, value: string) => void;
  resetCategory: (category: string) => void;
  toggleFilterSection: () => void;
  toggleSortMenu: (e: React.MouseEvent) => void;
  toggleGridMenu: (e: React.MouseEvent) => void;
  handleSortChange: (option: string) => void;
  handleViewModeChange: (mode: string) => void;
}

// Context'i oluştur
const CatalogContext = createContext<CatalogContextType | undefined>(undefined);

// Provider props
interface CatalogProviderProps {
  children: ReactNode;
  items: CatalogItem[];
  initialSortOption?: string;
  initialViewMode?: "grid4" | "grid6" | "grid8" | "table" | "gallery";
  filterFunction?: (
    item: CatalogItem,
    searchValue: string,
    selectedTags: string[],
    selectedFilters: SelectedFiltersType
  ) => boolean;
  // Dışarıdan gelen filtre durumu ve değişiklik işleyicisi
  selectedFilters?: SelectedFiltersType;
  onFilterChange?: (filterKey: string, values: string[]) => void;
}

// Varsayılan filtreleme fonksiyonu
const defaultFilterFunction = (
  _item: CatalogItem,
  _searchValue: string,
  _selectedTags: string[],
  _selectedFilters: SelectedFiltersType
): boolean => {
  // Client-side filtreleme iptal edildi
  // Filtreleme veritabanı sorgusu ile yapılacak
  return true;
};

// Provider bileşeni
export function CatalogProvider({
  children,
  items,
  initialSortOption = "newest",
  initialViewMode = "grid6",
  filterFunction = defaultFilterFunction,
  selectedFilters: externalSelectedFilters,
  onFilterChange,
}: CatalogProviderProps) {
  // State yönetimi
  const [searchValue, setSearchValue] = useState("");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [isFilterSectionVisible, setIsFilterSectionVisible] = useState(false);
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [isGridOpen, setIsGridOpen] = useState(false);
  const [sortOption, setSortOption] = useState(initialSortOption);
  const [viewMode, setViewMode] = useState<
    "grid4" | "grid6" | "grid8" | "table" | "gallery"
  >(initialViewMode);

  // Dışarıdan gelen filtre durumunu kullan veya yerel durumu oluştur
  const [internalSelectedFilters, setInternalSelectedFilters] = useState<SelectedFiltersType>({
    medium: [],
    category: [],
    chains: [],
    years: [],
    type: [],
    platform: [],
  });

  // Kullanılacak filtre durumunu belirle
  const selectedFilters = externalSelectedFilters || internalSelectedFilters;

  // Etiket değiştirme fonksiyonu
  const toggleTag = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  // Tüm filtreleri temizleme
  const clearFilters = () => {
    setSearchValue("");
    setSelectedTags([]);
    
    if (onFilterChange) {
      // Her filtre kategorisi için boş dizi gönder
      Object.keys(selectedFilters).forEach(key => {
        onFilterChange(key, []);
      });
    } else {
      setInternalSelectedFilters({
        medium: [],
        category: [],
        chains: [],
        years: [],
        type: [],
        platform: [],
      });
    }
  };

  // Filtre değişikliği işleme
  const handleFilterChange = (category: string, value: string) => {
    if (onFilterChange) {
      // Dışarıdan gelen değişiklik işleyicisini kullan
      const newValues = selectedFilters[category].includes(value)
        ? selectedFilters[category].filter(item => item !== value)
        : [...selectedFilters[category], value];
      
      onFilterChange(category, newValues);
    } else {
      // İç durumu güncelle
      setInternalSelectedFilters(prev => {
        // Değer zaten seçiliyse, kaldır
        if (prev[category].includes(value)) {
          return {
            ...prev,
            [category]: prev[category].filter((item) => item !== value),
          };
        }
        // Değilse, ekle
        return {
          ...prev,
          [category]: [...prev[category], value],
        };
      });
    }
  };

  // Kategori sıfırlama
  const resetCategory = (category: string) => {
    if (onFilterChange) {
      // Dışarıdan gelen değişiklik işleyicisini kullan
      onFilterChange(category, []);
    } else {
      // İç durumu güncelle
      setInternalSelectedFilters(prev => ({
        ...prev,
        [category]: [],
      }));
    }
  };

  // Filtre bölümünü aç/kapat
  const toggleFilterSection = () => {
    setIsFilterSectionVisible(!isFilterSectionVisible);
    // Diğer açılır menüleri kapat
    if (isSortOpen) setIsSortOpen(false);
    if (isGridOpen) setIsGridOpen(false);
  };

  // Sıralama menüsünü aç/kapat
  const toggleSortMenu = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsSortOpen(!isSortOpen);
    // Diğer açılır menüleri kapat
    if (isFilterSectionVisible) setIsFilterSectionVisible(false);
    if (isGridOpen) setIsGridOpen(false);
  };

  // Grid menüsünü aç/kapat
  const toggleGridMenu = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsGridOpen(!isGridOpen);
    // Diğer açılır menüleri kapat
    if (isFilterSectionVisible) setIsFilterSectionVisible(false);
    if (isSortOpen) setIsSortOpen(false);
  };

  // Sıralama değişikliği işleme
  const handleSortChange = (option: string) => {
    setSortOption(option);
    setIsSortOpen(false);
  };

  // Görünüm modu değişikliği işleme
  const handleViewModeChange = (mode: string) => {
    setViewMode(mode as "grid4" | "grid6" | "grid8" | "table" | "gallery");
    setIsGridOpen(false);
  };

  // Filtrelenmiş öğeleri hesapla
  const filteredItems = items.filter((item) =>
    filterFunction(item, searchValue, selectedTags, selectedFilters)
  );

  // Context değerlerini hazırla
  const contextValue: CatalogContextType = {
    searchValue,
    selectedTags,
    isFilterSectionVisible,
    isSortOpen,
    isGridOpen,
    sortOption,
    viewMode,
    selectedFilters,
    filteredItems,
    setSearchValue,
    toggleTag,
    clearFilters,
    handleFilterChange,
    resetCategory,
    toggleFilterSection,
    toggleSortMenu,
    toggleGridMenu,
    handleSortChange,
    handleViewModeChange,
  };

  return (
    <CatalogContext.Provider value={contextValue}>
      {children}
    </CatalogContext.Provider>
  );
}

// Custom hook
export function useCatalog() {
  const context = useContext(CatalogContext);
  if (context === undefined) {
    throw new Error("useCatalog must be used within a CatalogProvider");
  }
  return context;
}
